import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Clock, Send, Sparkles, Star } from "lucide-react";
import { SERVICE_OPTIONS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

export default function Booking() {
  const [form, setForm] = useState({ name: "", email: "", service: SERVICE_OPTIONS[0], question: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.question.trim()) {
      toast.error("Please share your name, email, and question.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setDone(true);
      toast.success("Received. Fairy Yasha will reach you within 24 hours.");
      setForm({ name: "", email: "", service: SERVICE_OPTIONS[0], question: "" });
    } catch (err) {
      toast.error("The connection wavered. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-sm bg-[#140B09] border border-[#C9A24B]/25 px-4 py-3 text-[#EFE3CC] placeholder:text-[#7a6a55] outline-none focus:border-[#C77E8A] focus:ring-1 focus:ring-[#C77E8A]/40 transition-all";

  return (
    <section id="contact" className="relative py-24 lg:py-36 overflow-hidden" data-testid="booking-section">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-[#9C2A52]/12 blur-[120px] -z-10" />

      <div className="mx-auto max-w-[900px] px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center text-center mb-12"
        >
          <span className="text-[11px] tracking-[0.4em] uppercase text-[#C9A24B] mb-5">✦ Work With Me ✦</span>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[#EFE3CC]">
            Leave your <span className="italic text-gold-gradient">question</span>
          </h2>
          <div className="mt-6 flex items-center gap-3 rounded-full border border-[#C9A24B]/30 bg-[#1F130E] px-5 py-2.5" data-testid="live-chat-badge">
            <Clock className="w-4 h-4 text-[#C9A24B]" strokeWidth={1.5} />
            <span className="text-sm text-[#EFE3CC]">Live chat reply within <span className="text-[#E9D3A0]">24 hours</span></span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.9, ease }}
          className="gold-frame rounded-sm bg-[#1F130E] p-7 lg:p-10"
        >
          {done ? (
            <div className="flex flex-col items-center text-center py-10" data-testid="booking-success">
              <div className="w-16 h-16 rounded-full border border-[#C9A24B]/40 flex items-center justify-center glow-gold mb-6">
                <Star className="w-7 h-7 text-[#C9A24B]" fill="#C9A24B" strokeWidth={0} />
              </div>
              <h3 className="font-display text-3xl text-[#EFE3CC] mb-3">Your message is sent</h3>
              <p className="text-[#B7A488] font-light max-w-xs">
                Your question now rests with Fairy Yasha. Watch your inbox — a live chat will open within 24 hours.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="booking-reset"
                className="mt-8 text-xs tracking-[0.2em] uppercase text-[#E9D3A0] border-b border-transparent hover:border-[#C9A24B] transition-colors pb-1"
              >
                Send another question
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="booking-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[11px] tracking-[0.15em] uppercase text-[#B7A488] mb-2">Your Name</label>
                  <input type="text" value={form.name} onChange={update("name")} data-testid="booking-name" placeholder="How I should call you" className={inputCls} />
                </div>
                <div>
                  <label className="block text-[11px] tracking-[0.15em] uppercase text-[#B7A488] mb-2">Email</label>
                  <input type="email" value={form.email} onChange={update("email")} data-testid="booking-email" placeholder="where guidance finds you" className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-[11px] tracking-[0.15em] uppercase text-[#B7A488] mb-2">Chosen Path</label>
                <select value={form.service} onChange={update("service")} data-testid="booking-service" className={`${inputCls} appearance-none cursor-pointer`}>
                  {SERVICE_OPTIONS.map((o) => (
                    <option key={o} value={o} className="bg-[#1F130E]">{o}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] tracking-[0.15em] uppercase text-[#B7A488] mb-2">Your Question</label>
                <textarea value={form.question} onChange={update("question")} data-testid="booking-question" rows={4} placeholder="What weighs on your spirit?" className={`${inputCls} resize-none`} />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="booking-submit-button"
                className="group relative w-full overflow-hidden rounded-full bg-gradient-to-r from-[#9C2A52] to-[#C77E8A] py-4 text-xs tracking-[0.2em] uppercase text-[#EFE3CC] shadow-[0_0_30px_rgba(156,42,82,0.3)] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {loading ? (<><Sparkles className="w-4 h-4 animate-pulse" strokeWidth={1.5} /> Sending…</>) : (<><Send className="w-4 h-4" strokeWidth={1.5} /> Send My Question</>)}
                </span>
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
